import {useEffect, useState} from 'react';
import {ArrowPathIcon, EnvelopeIcon, LockClosedIcon} from '@heroicons/react/24/outline';
import FormModal from '../../../../../components/general/FormModal.tsx';
import {Switch} from '../../../../../components/general/Switch.tsx';
import {cn} from '../../../../../lib/utils.ts';
import {fieldLabelClasses, fieldVariants} from '../../../../../components/general/fieldStyles.ts';
import {composeInvoiceEmailMessage} from './utils/invoiceEmailMessage.ts';

export type SendInvoiceEmailKind = 'send' | 'resend' | 'reminder';

export interface SendInvoiceEmailValues {
    toEmail: string;
    ccEmail: string;
    bccEmail: string;
    replyToEmail: string;
    message: string;
    messageBody: string;
    rememberCc: boolean;
    rememberMessage: boolean;
}

interface Props {
    open: boolean;
    kind: SendInvoiceEmailKind;
    invoiceNumber: string | null;
    replyToEmail: string;
    initialToEmail?: string | null;
    initialCcEmail?: string | null;
    initialBccEmail?: string | null;
    intro: string;
    defaultBody?: string;
    attachmentFileName?: string | null;
    attachmentAmountLabel?: string | null;
    isSubmitting?: boolean;
    onClose: () => void;
    onSubmit: (values: SendInvoiceEmailValues) => void;
}

const TITLES: Record<SendInvoiceEmailKind, string> = {
    send: 'Factuur versturen',
    resend: 'Factuur opnieuw versturen',
    reminder: 'Betalingsherinnering versturen',
};

const SUBTITLES: Record<SendInvoiceEmailKind, string> = {
    send: 'Verstuur',
    resend: 'Verstuur',
    reminder: 'Verstuur een herinnering voor',
};

const SUBMIT_LABELS: Record<SendInvoiceEmailKind, string> = {
    send: 'Verstuur factuur',
    resend: 'Verstuur opnieuw',
    reminder: 'Verstuur herinnering',
};

const FORBIDDEN_CONTENT = /(<\s*[a-zA-Z!/])|(https?:\/\/)|(www\.)|(ftp:\/\/)|(data:)|(base64,)/i;
const MAXIMUM_MESSAGE_LENGTH = 2000;
const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export function SendInvoiceEmailModal({
    open,
    kind,
    invoiceNumber,
    replyToEmail,
    initialToEmail,
    initialCcEmail,
    initialBccEmail,
    intro,
    defaultBody,
    attachmentFileName,
    attachmentAmountLabel,
    isSubmitting,
    onClose,
    onSubmit,
}: Props) {
    const [toEmail, setToEmail] = useState('');
    const [ccEmail, setCcEmail] = useState('');
    const [bccEmail, setBccEmail] = useState('');
    const [replyTo, setReplyTo] = useState('');
    const [body, setBody] = useState('');
    const [rememberCc, setRememberCc] = useState(false);
    const [rememberMessage, setRememberMessage] = useState(false);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        if (open) {
            setToEmail(initialToEmail ?? '');
            setCcEmail(initialCcEmail ?? '');
            setBccEmail(initialBccEmail ?? '');
            setReplyTo(replyToEmail);
            setBody(defaultBody ?? '');
            setRememberCc(false);
            setRememberMessage(false);
            setError(null);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open]);

    const bodyDiffersFromDefault = body.trim() !== (defaultBody ?? '').trim();
    const remainingBodyLength = Math.max(
        0, MAXIMUM_MESSAGE_LENGTH - composeInvoiceEmailMessage(intro, '').length - 2,
    );

    function validateAndSubmit() {
        if (!EMAIL_PATTERN.test(toEmail.trim())) {
            setError('Vul een geldig e-mailadres in bij "Aan".');
            return;
        }
        if (ccEmail.trim() && !EMAIL_PATTERN.test(ccEmail.trim())) {
            setError('Vul een geldig e-mailadres in bij "CC", of laat het veld leeg.');
            return;
        }
        if (bccEmail.trim() && !EMAIL_PATTERN.test(bccEmail.trim())) {
            setError('Vul een geldig e-mailadres in bij "BCC", of laat het veld leeg.');
            return;
        }
        if (!EMAIL_PATTERN.test(replyTo.trim())) {
            setError('Vul een geldig e-mailadres in bij "Antwoorden naar".');
            return;
        }
        if (FORBIDDEN_CONTENT.test(intro)) {
            setError('De klantnaam bevat een link of HTML. Pas de klantgegevens aan en probeer het opnieuw.');
            return;
        }
        if (FORBIDDEN_CONTENT.test(body)) {
            setError('Het bericht mag alleen tekst bevatten: links, afbeeldingen en HTML zijn niet toegestaan.');
            return;
        }
        setError(null);
        onSubmit({
            toEmail: toEmail.trim(),
            ccEmail: ccEmail.trim(),
            bccEmail: bccEmail.trim(),
            replyToEmail: replyTo.trim(),
            message: composeInvoiceEmailMessage(intro, body),
            messageBody: body.trim(),
            rememberCc: rememberCc && ccEmail.trim() !== '',
            rememberMessage,
        });
    }

    return (
        <FormModal
            open={open}
            onClose={onClose}
            onSubmit={validateAndSubmit}
            title={TITLES[kind]}
            subtitle={
                invoiceNumber
                    ? `${SUBTITLES[kind]} ${invoiceNumber} per e-mail.`
                    : undefined
            }
            submitLabel={SUBMIT_LABELS[kind]}
            isSubmitting={isSubmitting}
            width="xl"
        >
            <div className="flex flex-col gap-4">
                <div>
                    <label htmlFor="send-invoice-to" className={cn(fieldLabelClasses, 'mb-2 block')}>
                        Aan
                    </label>
                    <input
                        id="send-invoice-to"
                        type="email"
                        className={cn(fieldVariants(), 'w-full')}
                        placeholder="klant@bedrijf.nl"
                        value={toEmail}
                        onChange={event => setToEmail(event.target.value)}
                    />
                </div>

                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                    <div>
                        <label htmlFor="send-invoice-cc" className={cn(fieldLabelClasses, 'mb-2 block')}>
                            CC
                        </label>
                        <input
                            id="send-invoice-cc"
                            type="email"
                            className={cn(fieldVariants(), 'w-full')}
                            placeholder="crediteuren@klant.nl"
                            value={ccEmail}
                            onChange={event => setCcEmail(event.target.value)}
                        />
                    </div>
                    <div>
                        <label htmlFor="send-invoice-bcc" className={cn(fieldLabelClasses, 'mb-2 block')}>
                            BCC
                        </label>
                        <input
                            id="send-invoice-bcc"
                            type="email"
                            className={cn(fieldVariants(), 'w-full')}
                            placeholder="administratie@bedrijf.nl"
                            value={bccEmail}
                            onChange={event => setBccEmail(event.target.value)}
                        />
                    </div>
                    <div>
                        <label htmlFor="send-invoice-reply-to" className={cn(fieldLabelClasses, 'mb-2 block')}>
                            Antwoorden naar
                        </label>
                        <input
                            id="send-invoice-reply-to"
                            type="email"
                            className={cn(fieldVariants(), 'w-full')}
                            placeholder={replyToEmail}
                            value={replyTo}
                            onChange={event => setReplyTo(event.target.value)}
                        />
                    </div>
                </div>

                <div className="flex items-start gap-3">
                    <Switch
                        checked={rememberCc}
                        onChange={setRememberCc}
                        disabled={ccEmail.trim() === ''}
                        aria-label="Onthoud CC bij deze klant"
                    />
                    <div className="-mt-0.5">
                        <div className="text-sm text-gray-900">Onthoud CC bij deze klant</div>
                        <div className="text-xs text-gray-500">
                            Volgende facturen gaan automatisch ook naar dit adres
                        </div>
                    </div>
                </div>

                {attachmentFileName && (
                    <div className="flex items-center gap-2 rounded-md bg-gray-50 px-3 py-2.5 text-sm">
                        <EnvelopeIcon className="h-5 w-5 flex-shrink-0 text-primary"/>
                        <span className="font-semibold text-gray-900">{attachmentFileName}</span>
                        {attachmentAmountLabel && (
                            <span className="text-gray-700">· {attachmentAmountLabel}</span>
                        )}
                    </div>
                )}

                <div>
                    <div className="mb-2 flex items-center justify-between gap-2">
                        <label htmlFor="send-invoice-message" className={fieldLabelClasses}>
                            Bericht
                        </label>
                        {bodyDiffersFromDefault && (defaultBody ?? '') !== '' && (
                            <button
                                type="button"
                                onClick={() => setBody(defaultBody ?? '')}
                                className="flex items-center gap-1 text-xs font-semibold text-primary hover:opacity-80"
                            >
                                <ArrowPathIcon className="h-4 w-4"/>
                                Standaardtekst
                            </button>
                        )}
                    </div>
                    {intro.trim() !== '' && (
                        <div
                            data-testid="send-invoice-intro"
                            className={cn(
                                'box-border w-full whitespace-pre-line rounded-t-md border-[0.5px]',
                                'border-b-0 border-gray-300 bg-gray-50 px-3 py-2 text-sm text-gray-500',
                            )}
                        >
                            <span className="mb-1 flex items-center gap-1 text-[10.5px] font-semibold uppercase tracking-[0.05em] text-gray-400">
                                <LockClosedIcon className="h-3 w-3"/>
                                Wordt automatisch ingevuld
                            </span>
                            {intro}
                        </div>
                    )}
                    <textarea
                        id="send-invoice-message"
                        rows={5}
                        maxLength={remainingBodyLength}
                        className={cn(
                            fieldVariants(), 'h-auto w-full py-2',
                            intro.trim() !== '' && 'rounded-t-none',
                        )}
                        placeholder="Alleen tekst; links en afbeeldingen zijn niet toegestaan."
                        value={body}
                        onChange={event => setBody(event.target.value)}
                    />
                    <p className="mt-1.5 text-xs text-gray-500">
                        De aanhef en de factuurregel vullen we per factuur zelf in, met de juiste klantnaam
                        en het juiste factuurnummer. Alleen de tekst die je hierboven typt kun je als
                        standaardtekst bewaren.
                    </p>
                </div>

                <div className="flex items-start gap-3">
                    <Switch
                        checked={rememberMessage}
                        onChange={setRememberMessage}
                        aria-label="Onthoud als standaardtekst"
                    />
                    <div className="-mt-0.5">
                        <div className="text-sm text-gray-900">Onthoud als standaardtekst</div>
                        <div className="text-xs text-gray-500">
                            Gebruik deze tekst voortaan voor nieuwe facturen
                        </div>
                    </div>
                </div>

                {error && <p className="text-sm text-red-600">{error}</p>}

                <p className="text-xs text-gray-500">
                    De factuur-PDF en het UBL-bestand (XML) worden automatisch bijgevoegd.
                </p>
            </div>
        </FormModal>
    );
}
